import { type Currency } from "@prisma/client";
import { value } from "~/components/helpers";
import { chain, get, maxBy, minBy } from "~/lib/lodash";
import { isNotNull } from "~/lib/utils";
import { computeEmployeeCompensation, type Measure } from "~/services/employee";
import { type MarketDataStats, type MarketDataStatsFromDataset } from "~/services/marketCompensationStats";

const ABSOLUTE_MIN = 30_000_00;
const ABSOLUTE_MAX = 300_000_00;

const BASE_UNIT_OF_ACCOUNT = 1000;

export const useMarketData = ({ marketDataMeasure, currency }: { marketDataMeasure: Measure; currency: Currency }) => {
  const getMarketValue = (stats: MarketDataStats) => {
    return value(() => {
      const measure = get(stats, marketDataMeasure);

      return measure ?? stats.totalCash;
    });
  };

  const computeMinMarketValue = (series: (MarketDataStats | null)[]) => {
    const mins = series
      .filter((stats): stats is MarketDataStats => {
        return !!stats && getMarketValue(stats).min > 0;
      })
      .map((stats) => {
        return getMarketValue(stats).min;
      });

    const baseMin = mins.length ? (minBy(mins) as number) : ABSOLUTE_MIN;

    const employees = chain(series)
      .filter((stats): stats is MarketDataStatsFromDataset => !!stats)
      .filter((serie) => get(serie, "employees.length", 0) > 0)
      .map((serie) => serie?.companyEmployees)
      .flatten()
      .compact()
      .value();

    if (employees) {
      const employeesCompensations = employees.map((employee) =>
        computeEmployeeCompensation(employee, {
          measure: marketDataMeasure,
          targetCurrency: currency,
        })
      );

      return Math.min(baseMin, ...employeesCompensations);
    }

    return baseMin;
  };

  const computeMaxMarketValue = (series: (MarketDataStats | null)[]) => {
    const maxes = series.filter(isNotNull).map((stats) => {
      return getMarketValue(stats).max;
    });

    const baseMax = maxes.length ? (maxBy(maxes) as number) : ABSOLUTE_MAX;

    const employees = chain(series)
      .filter((stats): stats is MarketDataStatsFromDataset => !!stats)
      .filter((serie) => get(serie, "employees.length", 0) > 0)
      .map((serie) => serie?.companyEmployees)
      .flatten()
      .compact()
      .value();

    if (employees) {
      const employeesCompensations = employees.map((employee) =>
        computeEmployeeCompensation(employee, {
          measure: marketDataMeasure,
          targetCurrency: currency,
        })
      );

      return Math.max(baseMax, ...employeesCompensations);
    }

    return baseMax;
  };

  const getChartUnit = (min: number) => {
    let unit = BASE_UNIT_OF_ACCOUNT;
    while (min / unit >= 10) {
      unit = unit * 10;
    }

    return unit;
  };

  const getMarketBoundaries = (series: (MarketDataStats | null)[]) => {
    const baseMin = computeMinMarketValue(series);
    const baseMax = computeMaxMarketValue(series);
    const unit = getChartUnit(baseMin);

    const roundedMin = Math.floor(baseMin / unit - 0.1) * unit;
    const roundedMax = Math.ceil(baseMax / unit + 0.1) * unit;
    const amplitude = roundedMax - roundedMin;

    return { min: roundedMin, max: roundedMax, unit, amplitude };
  };

  return { getMarketValue, getMarketBoundaries, getChartUnit };
};
